import {
  firestore,
  doc,
  collection,
  runTransaction,
  serverTimestamp,
} from "@/firebase/init";

export function roundMoney(value) {
  return Number(parseFloat(value ?? 0).toFixed(2));
}

export class InsufficientPromotionCreditBalanceError extends Error {
  constructor(available, amount) {
    const availableRounded = roundMoney(available);
    const amountRounded = roundMoney(amount);
    super(
      availableRounded <= 0
        ? "This promotion credit has no remaining balance. Refresh and try again."
        : `Payment amount (${amountRounded}) exceeds credit available (${availableRounded}). Refresh and try again.`,
    );
    this.name = "InsufficientPromotionCreditBalanceError";
    this.available = availableRounded;
    this.amount = amountRounded;
  }
}

/**
 * Atomically write a promotion-credit payment and decrement credit.available.
 * Concurrent applies lose when available is already claimed.
 *
 * Order due / paid / A/R stay in applyPayment's writeBatch so earlier payments
 * in the same submission (still uncommitted) are reflected correctly.
 */
export async function claimAndWritePromotionCreditPayment({
  creditId,
  amount,
  paymentData,
  user,
} = {}) {
  if (!creditId) {
    throw new Error("Promotion credit id is required.");
  }
  if (!paymentData || typeof paymentData !== "object") {
    throw new Error("Payment data is required.");
  }
  if (!user?.uid) {
    throw new Error("User is required to apply a promotion credit.");
  }

  const paymentAmount = roundMoney(amount);
  if (paymentAmount <= 0) {
    throw new Error("Payment amount must be greater than zero.");
  }

  const creditRef = doc(firestore, "promotionCredits", creditId);
  const paymentRef = doc(collection(firestore, "salesOrders_payments"));

  return runTransaction(firestore, async (transaction) => {
    const creditSnap = await transaction.get(creditRef);
    if (!creditSnap.exists()) {
      throw new Error("Promotion credit not found. Refresh and try again.");
    }

    const available = roundMoney(creditSnap.data().available);
    if (available < paymentAmount) {
      throw new InsufficientPromotionCreditBalanceError(
        available,
        paymentAmount,
      );
    }

    const newAvailable = roundMoney(available - paymentAmount);
    const actor = {
      name: user.name,
      uid: user.uid,
    };

    transaction.set(paymentRef, {
      ...paymentData,
      amount: paymentAmount,
      promotionCredit: creditId,
      paymentType: "promotionCredit",
      created: serverTimestamp(),
      updated: serverTimestamp(),
      createdBy: actor,
      // Force reversed:false after spread so callers cannot override it.
      flags: {
        ...(paymentData.flags || {}),
        reversed: false,
      },
    });

    transaction.set(
      creditRef,
      {
        updated: serverTimestamp(),
        lastUpdated: actor,
        active: newAvailable > 0,
        available: newAvailable,
      },
      { merge: true },
    );

    return {
      paymentId: paymentRef.id,
      previousAvailable: available,
      newAvailable,
    };
  });
}
